import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { cn } from "@/cn";
import { CoMark } from "@/components/co-mark";
import { MobileNav } from "@/components/mobile-nav";
import { ThemeToggle } from "@/components/theme-toggle";
import { TopAppBar } from "@/components/top-app-bar";
import { SidebarNotes } from "@/components/sidebar-notes";
import { instrumentSerif } from "@/lib/fonts";
import {
  NAV_SECTIONS,
  isActivePath,
  loadNavCollapsed,
  persistNavCollapsed,
} from "@/lib/nav-items";

export function AppShell({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const pathname = location.pathname;
  const assistantMode = pathname.startsWith("/ai-assistant");
  const [collapsed, setCollapsed] = useState(() => loadNavCollapsed());

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      persistNavCollapsed(next);
      return next;
    });
  }

  return (
    <>
      <MobileNav />
      <div className="flex min-h-screen">
        <aside
          className={cn(
            "sticky top-0 hidden h-screen shrink-0 flex-col border-r border-border bg-surface/30 p-3 md:flex",
            collapsed ? "w-14" : "w-60 p-4",
          )}
        >
          <div
            className={cn(
              "mb-6 flex shrink-0 items-center",
              collapsed ? "flex-col gap-2" : "justify-between gap-2",
            )}
          >
            <Link
              to="/"
              className={cn(
                "flex items-center gap-2.5 px-1",
                collapsed && "justify-center px-0",
              )}
              title="career-ops"
            >
              <CoMark size={collapsed ? 28 : 32} />
              {!collapsed && (
                <span
                  className={`${instrumentSerif.className} relative -top-px text-2xl font-normal tracking-tight text-landing`}
                >
                  career-ops
                </span>
              )}
            </Link>
            <button
              type="button"
              onClick={toggleCollapsed}
              className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-surface-hover hover:text-foreground"
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? (
                <PanelLeftOpen className="size-4" />
              ) : (
                <PanelLeftClose className="size-4" />
              )}
            </button>
          </div>

          <nav className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
            {NAV_SECTIONS.map((section) => (
              <div key={section.id} className="flex flex-col gap-1">
                {section.label && !collapsed ? (
                  <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-faint">
                    {section.label}
                  </p>
                ) : null}
                {section.items.map(({ href, label, icon: Icon, chip }) => {
                  const active = isActivePath(href, pathname);
                  return (
                    <Link
                      key={href}
                      to={href}
                      title={label}
                      className={cn(
                        "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                        collapsed && "justify-center px-2",
                        active
                          ? "bg-brand-soft text-brand"
                          : "text-muted-foreground hover:bg-surface-hover hover:text-foreground",
                      )}
                    >
                      <Icon className="size-4 shrink-0" />
                      {!collapsed && (
                        <>
                          {label}
                          {chip && (
                            <span className="ml-auto rounded-full border border-brand/30 bg-brand-soft px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-brand">
                              {chip}
                            </span>
                          )}
                        </>
                      )}
                    </Link>
                  );
                })}
              </div>
            ))}
          </nav>

          <div className="mt-3 shrink-0 space-y-3">
            <SidebarNotes collapsed={collapsed} />
            <div
              className={cn(
                "flex items-center px-1",
                collapsed ? "justify-center" : "justify-between",
              )}
            >
              {!collapsed && (
                <span
                  className={`${instrumentSerif.className} text-sm text-faint`}
                >
                  local-first · v0
                </span>
              )}
              <ThemeToggle />
            </div>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <TopAppBar />
          <main
            className={cn(
              // overflow-x-clip avoids creating a scroll containment that breaks position:sticky
              "flex-1 overflow-x-clip",
              assistantMode && "flex min-h-0 flex-col md:h-[calc(100vh-3rem)]",
            )}
          >
            {assistantMode ? (
              <div className="min-h-0 flex-1">{children}</div>
            ) : (
              <div className="mx-auto max-w-6xl px-6 py-8">{children}</div>
            )}
          </main>
        </div>
      </div>
    </>
  );
}
